package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.ThemeArgument
import top.wcpe.allincore.dsl.compiler.ast.ThemeAst
import top.wcpe.allincore.dsl.compiler.ast.ThemeMetadataAst
import top.wcpe.allincore.dsl.compiler.ast.ThemeValue
import top.wcpe.allincore.dsl.compiler.ast.ThemeTokenAst
import top.wcpe.allincore.dsl.compiler.ast.ModifierBundleAst
import top.wcpe.allincore.dsl.compiler.ast.DslMode
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.diag.MigrationConfidence
import top.wcpe.allincore.dsl.compiler.diag.MigrationHint
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * 解析 `theme { name = literal ... }` 块。
 *
 * 仅接受字面量值：String / Number / Boolean。
 * - Number 统一存为 Double（不区分整型/浮点），并允许 `- NUMBER` 形式的负数字面量。
 * - 重复 key 立即抛 [ParseException]。
 *
 * 嵌套 object 支持（M-Toast Stage 1）：
 *
 * ```
 * theme {
 *   primary = "#FFFFFF"
 *   defaultToastStyle {
 *     width = 300
 *     anchor = "top"
 *   }
 *   toastStyle {
 *     warning {
 *       backgroundColor = "#FFAA00"
 *     }
 *   }
 * }
 * ```
 *
 * 嵌套字段 token 在内部以 dotted key 展平存储：上例展平为
 * `defaultToastStyle.width = 300.0`、`defaultToastStyle.anchor = "top"`、
 * `toastStyle.warning.backgroundColor = "#FFAA00"` 等。展平后与扁平
 * dotted key 完全等价，与既有 IR `themeTokens` 存储约定保持兼容（值仍是
 * String/Double/Boolean）。
 */
internal class ThemeBlockParser(
    private val p: Parser,
    private val dslMode: DslMode = DslMode.LEGACY,
) {

    companion object {
        private val COLOR_LITERAL = Regex("^#(?:[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$")
    }

    private var metadata: ThemeMetadataAst? = null

    /** 最近一次 parse / parseBodyOnly 解析到的 theme metadata（文件级目录展示用）。 */
    fun lastMetadata(): ThemeMetadataAst? = metadata

    fun parse(): ThemeAst {
        val start = p.expect(TokenType.KW_THEME)
        return parseBodyOnly("theme", start.position)
    }

    fun parseBodyOnly(
        label: String,
        startPosition: top.wcpe.allincore.dsl.compiler.source.SourcePosition = p.current().position,
    ): ThemeAst {
        p.expect(TokenType.LBRACE, "theme 块缺少 '{'")
        p.skipNewlines()

        metadata = null
        val tokens = LinkedHashMap<String, Any>()
        val tokenNodes = LinkedHashMap<String, ThemeTokenAst>()
        val bundles = mutableListOf<ModifierBundleAst>()
        parseEntries(tokens, tokenNodes, bundles, prefix = "")

        val close = p.expect(TokenType.RBRACE, "$label 块缺少 '}'")
        // metadata 由 ThemeFileParser / ThemeCollectorPass 经 ThemeFileAst 传递；
        // ThemeAst 主构造保持 tokenNodes / sourcePosition sidecar ABI。
        return ThemeAst(tokens = tokens, modifierBundles = bundles).also {
            it.tokenNodes = tokenNodes
            it.sourcePosition = p.completePosition(startPosition, close)
        }
    }

    /**
     * 通用 `name = literal` / `name { ... }` 字段解析；按需递归处理嵌套 object。
     *
     * 调用方负责消费 `{`，本方法处理 `{` 与 `}` 之间的全部条目。
     * `prefix` 非空时所有 key 自动加上 `prefix + "."` 前缀展平到顶层 map。
     */
    private fun parseEntries(
        tokens: LinkedHashMap<String, Any>,
        tokenNodes: LinkedHashMap<String, ThemeTokenAst>,
        modifierBundles: MutableList<ModifierBundleAst>,
        prefix: String,
    ) {
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val nameTok = parseThemeFieldName()
            val name = nameTok.lexeme
            if (prefix.isEmpty() && isLegacyFlatComponentToken(name)) {
                if (dslMode == DslMode.STRICT) {
                    p.fail(DslError.AUI_V2_STRICT_FORBIDS_LEGACY_SYNTAX, nameTok.position, name)
                }
                p.warnMigration(
                    DslError.AUI_V2_LEGACY_DEPRECATED_SYNTAX,
                    nameTok.position,
                    MigrationHint("theme { <component> { ... } }", MigrationConfidence.SUGGESTED),
                    name,
                    "theme { <component> { ... } }",
                )
            }
            if (prefix.isEmpty() && name == "modifierBundles") {
                rejectV2InLegacy(nameTok)
                modifierBundles += ModifierBundleParser(p).parseAfterName()
                consumeEntrySeparator()
                continue
            }
            if (prefix.isEmpty() && name == "metadata") {
                if (metadata != null) p.fail("theme metadata 重复声明", nameTok.position)
                metadata = parseMetadata()
                consumeEntrySeparator()
                continue
            }
            val flatKey = if (prefix.isEmpty()) name else "$prefix.$name"

            if (isNamedDeclarationBlock(name) && p.peek(0).type == TokenType.IDENTIFIER && p.peek(1).type == TokenType.LBRACE) {
                rejectV2InLegacy(nameTok)
                val symbolTok = p.expect(TokenType.IDENTIFIER, "theme.$name 缺少静态名称")
                p.expect(TokenType.LBRACE, "theme.$name '${symbolTok.lexeme}' 后缺少 '{'")
                p.skipNewlines()
                val childPrefix = if (prefix.isEmpty()) symbolTok.lexeme else "$prefix.${symbolTok.lexeme}"
                parseEntries(tokens, tokenNodes, modifierBundles, childPrefix)
                p.expect(TokenType.RBRACE, "theme.$name '${symbolTok.lexeme}' 缺少 '}'")
            } else if (p.check(TokenType.LBRACE)) {
                // 嵌套 object：递归收集子字段并展平
                p.expect(TokenType.LBRACE)
                p.skipNewlines()
                parseEntries(tokens, tokenNodes, modifierBundles, flatKey)
                p.expect(TokenType.RBRACE, "theme 嵌套块 '$name' 缺少 '}'")
            } else {
                if (tokens.containsKey(flatKey)) {
                    p.fail("theme 字段 '$flatKey' 重复声明", nameTok.position)
                }
                p.expect(TokenType.EQUALS, "theme 字段 '$name' 后缺少 '='")
                val parsed = parseThemeValue()
                tokens[flatKey] = parsed.value
                tokenNodes[flatKey] = ThemeTokenAst(
                    name = flatKey,
                    value = parsed.value,
                    valuePosition = parsed.position,
                    position = nameTok.position,
                )
            }

            consumeEntrySeparator()
        }
    }

    private fun parseMetadata(): ThemeMetadataAst {
        p.expect(TokenType.LBRACE, "theme metadata 后缺少 '{'")
        p.skipNewlines()
        val fields = linkedMapOf<String, Any>()
        parseMetadataEntries(fields, "")
        p.expect(TokenType.RBRACE, "theme metadata 缺少 '}'")
        val allowed = setOf("displayName", "group", "order", "preview.primary", "preview.surface", "preview.onSurface")
        fields.keys.firstOrNull { it !in allowed }?.let { p.fail("theme metadata 未知字段 '$it'") }
        return ThemeMetadataAst(
            stringMetadata(fields, "displayName"),
            stringMetadata(fields, "group"),
            integerMetadata(fields, "order"),
            colorMetadata(fields, "preview.primary"),
            colorMetadata(fields, "preview.surface"),
            colorMetadata(fields, "preview.onSurface"),
        )
    }

    private fun parseMetadataEntries(fields: MutableMap<String, Any>, prefix: String) {
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val name = parseThemeFieldName().lexeme
            val key = if (prefix.isEmpty()) name else "$prefix.$name"
            if (fields.containsKey(key)) p.fail("theme metadata 字段 '$key' 重复声明")
            if (p.check(TokenType.LBRACE)) {
                if (key != "preview") p.fail("theme metadata 未知块 '$key'")
                p.advance()
                p.skipNewlines()
                parseMetadataEntries(fields, key)
                p.expect(TokenType.RBRACE, "theme metadata preview 缺少 '}'")
            } else {
                p.expect(TokenType.EQUALS, "theme metadata 字段 '$key' 后缺少 '='")
                fields[key] = parseThemeValue().value
            }
            consumeEntrySeparator()
        }
    }

    private fun stringMetadata(fields: Map<String, Any>, key: String): String =
        fields[key] as? String ?: p.fail("theme metadata 字段 '$key' 必须是字符串字面量")

    private fun integerMetadata(fields: Map<String, Any>, key: String): Int {
        val value = fields[key] as? Double ?: p.fail("theme metadata 字段 '$key' 必须是整数字面量")
        if (!value.isFinite() || value % 1.0 != 0.0 || value < Int.MIN_VALUE || value > Int.MAX_VALUE) {
            p.fail("theme metadata 字段 '$key' 必须是 32 位整数字面量")
        }
        return value.toInt()
    }

    private fun colorMetadata(fields: Map<String, Any>, key: String): String {
        val value = fields[key] as? String ?: p.fail("theme metadata 字段 '$key' 必须是颜色字面量")
        if (!COLOR_LITERAL.matches(value)) p.fail("theme metadata 字段 '$key' 必须是 #RRGGBB 或 #AARRGGBB")
        return value
    }

    private fun isNamedDeclarationBlock(name: String): Boolean =
        name == "variant" || name == "size" || name == "style"

    private fun parseThemeFieldName(): top.wcpe.allincore.dsl.compiler.lex.Token {
        val cur = p.current()
        if (cur.type == TokenType.IDENTIFIER || cur.type == TokenType.KW_COLOR || cur.type == TokenType.KW_DEFAULTS) {
            return p.advance()
        }
        p.fail("theme 字段名缺失：期望 IDENTIFIER，实际为 ${cur.type}（'${cur.lexeme}'）", cur.position)
    }

    private fun isLegacyFlatComponentToken(name: String): Boolean =
        name.startsWith("button") &&
            name.length > "button".length &&
            name["button".length].isUpperCase()

    private fun rejectV2InLegacy(nameTok: top.wcpe.allincore.dsl.compiler.lex.Token) {
        if (dslMode == DslMode.LEGACY) {
            p.fail(DslError.AUI_V2_LEGACY_FORBIDS_V2_SYNTAX, nameTok.position, nameTok.lexeme)
        }
    }

    private fun consumeEntrySeparator() {
        if (!p.check(TokenType.RBRACE)) {
            p.expect(TokenType.NEWLINE, "theme 字段后缺少换行")
            p.skipNewlines()
        }
    }

    /**
     * 解析 theme 字段值：字面量（字符串/数字/布尔）、token 引用（裸标识符 `primary`）、
     * 或颜色函数调用（`lighten(color, 0.1)`）。后两者以 [ThemeValue] 中间形态存入，
     * 由 ThemeValueResolverPass 在编译期解析为字面量。
     */
    private fun parseThemeValue(): ParsedThemeValue {
        val t = p.current()
        return when (t.type) {
            TokenType.STRING -> {
                p.advance()
                ParsedThemeValue(t.literalValue as String, t.position)
            }
            TokenType.NUMBER -> {
                p.advance()
                ParsedThemeValue((t.literalValue as Number).toDouble(), t.position)
            }
            TokenType.MINUS -> parseNegativeNumberLiteral()
            TokenType.BOOLEAN -> {
                p.advance()
                ParsedThemeValue(t.literalValue as Boolean, t.position)
            }
            TokenType.IDENTIFIER, TokenType.KW_THEME -> {
                val name = parseTokenPath()
                if (p.check(TokenType.LPAREN)) {
                    p.expect(TokenType.LPAREN)
                    p.skipNewlines()
                    val args = ArrayList<Any>()
                    if (!p.check(TokenType.RPAREN)) {
                        val firstArg = parseThemeValue()
                        args.add(ThemeArgument(firstArg.value, firstArg.position))
                        while (p.check(TokenType.COMMA)) {
                            p.expect(TokenType.COMMA)
                            p.skipNewlines()
                            val arg = parseThemeValue()
                            args.add(ThemeArgument(arg.value, arg.position))
                        }
                    }
                    p.expect(TokenType.RPAREN, "颜色函数 '$name' 缺少 ')'")
                    val position = p.completePosition(t.position)
                    ParsedThemeValue(ThemeValue.ColorCall(name, args, position), position)
                } else {
                    val position = p.completePosition(t.position)
                    ParsedThemeValue(ThemeValue.TokenRef(name.removePrefix("theme."), position), position)
                }
            }
            else -> p.fail("theme 字段值必须是字面量/token 引用/颜色函数，实际为 ${t.type}")
        }
    }

    private fun parseNegativeNumberLiteral(): ParsedThemeValue {
        val minus = p.advance()
        val numTok = p.expect(TokenType.NUMBER, "theme 负数字面量缺少数字")
        return ParsedThemeValue(
            -(numTok.literalValue as Number).toDouble(),
            minus.position.endingAt(numTok.range.end),
        )
    }

    private fun parseTokenPath(): String {
        val first = if (p.check(TokenType.KW_THEME)) p.advance() else p.expect(TokenType.IDENTIFIER)
        val parts = mutableListOf(first.lexeme)
        while (p.check(TokenType.DOT)) {
            p.advance()
            parts += p.expect(TokenType.IDENTIFIER, "theme token 引用缺少字段名").lexeme
        }
        return parts.joinToString(".")
    }

    private data class ParsedThemeValue(
        val value: Any,
        val position: top.wcpe.allincore.dsl.compiler.source.SourcePosition,
    )
}
